/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Injectable,
  OnInit,
  Input,
  Self,
  Optional,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  NgbDateStruct,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbCalendar,
} from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { ControlValueAccessor, NgControl } from '@angular/forms';
// class to format/parse the date coming from the input from/to NgbDateParserFormatter/local formatted date string
@Injectable()
export class LocalDateParserFormatter extends NgbDateParserFormatter {
  // parse data coming from input (not used when input is read only (default))
  parse(value: string): NgbDateStruct | null {
    if (value) {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      };

      let delimiter = '-',
        dayPos = 0,
        monthPos = 0,
        yearPos = 0;

      // get the parts for the current local
      const local = window.navigator.language || 'en-US';
      const localFormat = new Intl.DateTimeFormat(local, options);
      const partsFull = localFormat.formatToParts(new Date());

      // get the delimiter used between year, month, day
      partsFull.forEach((obj) => {
        if (obj.type == 'literal') {
          delimiter = obj.value;
        }
      });

      // generate array from partsFull without the delimiter objects
      const parts = partsFull.filter((obj) => {
        return obj.type != 'literal';
      });

      // set the index where the day, month, & year can be found in the local formatted date string (value)
      parts.forEach((obj, i) => {
        switch (obj.type) {
          case 'day':
            dayPos = i;
            break;
          case 'month':
            monthPos = i;
            break;
          case 'year':
            yearPos = i;
            break;
        }
      });

      // use dayPos, monthPos, & yearPos generated above to extract the day, month & year from value and generate ngbDateStruct
      const dateArr = value.split(delimiter);
      const year = dateArr[yearPos];
      const month = dateArr[monthPos];
      const day = dateArr[dayPos];
      const ngbDateStruct = {
        year: parseInt(year),
        month: parseInt(month),
        day: parseInt(day),
      };

      return ngbDateStruct;
    }
    return null;
  }

  // format NgbDateStruct into local date string format
  format(date: NgbDateStruct | null): string {
    if (date) {
      const local = window.navigator.language || 'en-US';
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      };
      let jsDate,
        localFormat = new Intl.DateTimeFormat(local, options),
        formattedDate;

      jsDate = new Date(date.year, date.month - 1, date.day, 12);
      // avoid 30 -> 1930 conversion
      jsDate.setFullYear(date.year);

      // create local formatted string
      formattedDate = localFormat.format(jsDate);

      return formattedDate;
    }
    return '';
  }
}

// class to localize the week day and month names
@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  calendarValues;

  private xdaTranslatePipe: XdaTranslate;
  constructor(private xdaStringsService: XdaStringsService) {
    super();

    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    // array to hold translated string for weekdays and months
    this.calendarValues = {
      weekdays: [
        this.xdaTranslatePipe.transform('SDE_MO'),
        this.xdaTranslatePipe.transform('SDE_TU'),
        this.xdaTranslatePipe.transform('SDE_WE'),
        this.xdaTranslatePipe.transform('SDE_TH'),
        this.xdaTranslatePipe.transform('SDE_FR'),
        this.xdaTranslatePipe.transform('SDE_SA'),
        this.xdaTranslatePipe.transform('SDE_SU'),
      ],
      months: [
        this.xdaTranslatePipe.transform('SDE_JANUARY'),
        this.xdaTranslatePipe.transform('SDE_FEBRUARY'),
        this.xdaTranslatePipe.transform('SDE_MARCH'),
        this.xdaTranslatePipe.transform('SDE_APRIL'),
        this.xdaTranslatePipe.transform('SDE_MAY'),
        this.xdaTranslatePipe.transform('SDE_JUNE'),
        this.xdaTranslatePipe.transform('SDE_JULY'),
        this.xdaTranslatePipe.transform('SDE_AUGUST'),
        this.xdaTranslatePipe.transform('SDE_SEPTEMBER'),
        this.xdaTranslatePipe.transform('SDE_OCTOBER'),
        this.xdaTranslatePipe.transform('SDE_NOVEMBER'),
        this.xdaTranslatePipe.transform('SDE_DECEMBER'),
      ],
    };
  }

  getWeekdayShortName(weekday: number): string {
    return this.calendarValues.weekdays[weekday - 1];
  }
  getMonthShortName(month: number): string {
    return this.calendarValues.months[month - 1];
  }
  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }
  getWeekdayLabel(weekday: number) {
    return this.calendarValues.weekdays[weekday - 1];
  }
  // localize the aria labels for each day in the calendar.
  getDayAriaLabel(date: NgbDateStruct): string {
    let local = window.navigator.language || 'en-US',
      options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      jsDate,
      localFormat = new Intl.DateTimeFormat(local, options),
      formattedDate;

    jsDate = new Date(date.year, date.month - 1, date.day, 12);
    // avoid 30 -> 1930 conversion
    jsDate.setFullYear(date.year);

    // create local formatted string
    formattedDate = localFormat.format(jsDate);

    return formattedDate;
  }
}

@Component({
  selector: 'xrx-calendar',
  templateUrl: './xrx-calendar.component.html',
  styleUrls: ['./xrx-calendar.component.less'],
  providers: [
    { provide: NgbDateParserFormatter, useClass: LocalDateParserFormatter },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
  standalone: false,
})
export class XrxCalendarComponent implements OnInit, ControlValueAccessor {
  @Input() readonly = true;
  @Input() maxDate: NgbDateStruct | null = null;
  @Input() minDate: NgbDateStruct | null = null;
  @Input() placeholder = '';
  @Input() error: string;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change = new EventEmitter<NgbDateStruct>();

  calendarModel: NgbDateStruct;
  calendarOpen = false;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private calendar: NgbCalendar,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {}

  writeValue(value: any): void {
    if (value) {
      this.calendarModel = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange(event: NgbDateStruct) {
    this.change.emit(event);
  }

  onTouched() {}

  opened() {
    this.calendarOpen = true;
  }

  closed() {
    this.calendarOpen = false;
  }

  onDateChanged(date: NgbDateStruct) {
    if (date) {
      this.onChange(date);
      this.change.emit(date);
    }
  }

  isErrorState(): boolean {
    if (this.ngControl.control) {
      return (
        this.ngControl.control.invalid &&
        this.ngControl.control.dirty &&
        !this.ngControl.control.untouched
      );
    } else {
      return false;
    }
  }

  hasAnyError(): boolean {
    return this.ngControl.control?.errors?.['customerror'];
  }
}
